import { isSolanaHandleInput } from "../hooks/useSolanaHandleRecipient";
import { WalletInlineButton } from "../../base-app/components/WalletInlineButton";
import type { useSolanaWithdrawalQuote } from "../hooks/useSolanaWithdrawalQuote";
import React from "react";
import { SolanaTestTokenNotice } from "./SolanaTestTokenNotice";
import { styles } from "../../styles";
import {
  formatSolanaTokenAmount,
  type SolanaNetworkConfig,
  type SolanaTokenInfo,
} from "../../config/solana";
import type { SolanaRecipientKind } from "../hooks/useSolanaActions";
import { classifySolanaRecipient } from "../hooks/useSolanaActions";
import { WalletAmountInput } from "../../base-app/components/WalletAmountInput";
import {
  WalletBatchRecipients,
  type WalletBatchRow,
} from "../../base-app/components/WalletBatchRecipients";
import { SolanaSendFeeSummary } from "./SolanaFeeSummary";
import { solanaBatchMax, solanaSendAmounts } from "../helpers/amounts";

interface RetiredTreeSummary {
  treeIndex: number;
  token: SolanaTokenInfo;
  total: string;
  count: number;
}
interface SolanaSendTabProps {
  handleStatus?: string | null;
  network: SolanaNetworkConfig;
  feeQuote: ReturnType<typeof useSolanaWithdrawalQuote>;
  tokens: SolanaTokenInfo[];
  selectedToken: SolanaTokenInfo;
  setSelectedToken: (token: SolanaTokenInfo) => void;
  recipient: string;
  setRecipient: (value: string) => void;
  recipientKind: SolanaRecipientKind;
  amount: string;
  setAmount: (value: string) => void;
  memo: string;
  setMemo: (value: string) => void;
  spendable: bigint;
  batchMode: boolean;
  setBatchMode: (value: boolean) => void;
  batchRows: WalletBatchRow[];
  setBatchRows: (rows: WalletBatchRow[]) => void;
  retiredTrees: RetiredTreeSummary[];
  connected: boolean;
  busy: boolean;
  onSend: () => void | Promise<void>;
  onBatchSend: () => void | Promise<void>;
  onMigrate: (
    treeIndex: number,
    token: SolanaTokenInfo,
  ) => void | Promise<void>;
}
export function SolanaSendTab({
  handleStatus,
  network,
  feeQuote,
  tokens,
  selectedToken,
  setSelectedToken,
  recipient,
  setRecipient,
  recipientKind,
  amount,
  setAmount,
  memo,
  setMemo,
  spendable,
  batchMode,
  setBatchMode,
  batchRows,
  setBatchRows,
  retiredTrees,
  connected,
  busy,
  onSend,
  onBatchSend,
  onMigrate,
}: SolanaSendTabProps) {
  const disabled = !connected || busy;
  const preview = solanaSendAmounts(
    network,
    selectedToken,
    batchMode ? batchRows.map((row) => row.amount) : [amount],
    spendable,
    batchMode ? "batch" : recipientKind,
    feeQuote.quote ? BigInt(feeQuote.quote.quote.fee) : null,
  );
  const invalidRecipient =
    batchMode &&
    batchRows.some(
      (row) => classifySolanaRecipient(row.recipient) !== "wallet",
    );
  const showError = batchMode
    ? batchRows.some((row) => row.amount.trim())
    : !!amount.trim();
  const error =
    feeQuote.error ||
    preview.error ||
    (invalidRecipient
      ? "Enter a Solana wallet address for every recipient."
      : null);
  return (
    <div>
      <p style={{ fontSize: "14px", color: "#b0c4de", marginBottom: "16px" }}>
        Enter a .stealth handle, private address or Solana wallet address to
        send privately or withdraw to a public wallet.
      </p>
      <label htmlFor="solana-send-token" style={styles.label}>
        Token
      </label>
      <select
        id="solana-send-token"
        value={selectedToken.mint ?? "sol"}
        onChange={(event) => {
          const token = tokens.find(
            (candidate) => (candidate.mint ?? "sol") === event.target.value,
          );
          if (token) setSelectedToken(token);
        }}
        style={styles.input}
      >
        {tokens.map((token) => (
          <option key={token.mint ?? "sol"} value={token.mint ?? "sol"}>
            {token.symbol} — {token.name}
          </option>
        ))}
      </select>
      <SolanaTestTokenNotice token={selectedToken} />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <label
          htmlFor={batchMode ? undefined : "solana-send-recipient"}
          style={styles.label}
        >
          {batchMode ? "Recipients" : "Recipient"}
        </label>
        <WalletInlineButton
          type="button"
          style={{ marginBottom: 6 }}
          disabled={disabled}
          onClick={() => setBatchMode(!batchMode)}
        >
          {batchMode ? "Back to single" : "+ Batch"}
        </WalletInlineButton>
      </div>
      {batchMode ? (
        <>
          <p className="wallet-review-context">
            Batch withdrawals pay each recipient the entered amount. The relayer
            fee is added to the total.
          </p>
          <p role="status" className="wallet-review-context">
            {batchRows.length < 2
              ? "Add at least two recipients to enable MAX."
              : invalidRecipient
                ? "Enter all recipient wallet addresses to enable MAX."
                : feeQuote.batchMaxError
                  ? `MAX unavailable: ${feeQuote.batchMaxError}`
                  : feeQuote.batchMaxLoading
                    ? "Calculating MAX after fees…"
                    : "MAX uses the balance remaining after fees and the other recipient amounts."}
          </p>
          <WalletBatchRecipients
            rows={batchRows}
            onChange={setBatchRows}
            disabled={disabled}
            placeholder="Solana wallet address"
            maxForRow={(index) => {
              const max = solanaBatchMax(
                selectedToken,
                feeQuote.batchMaxTotal,
                batchRows
                  .filter((_, i) => i !== index)
                  .map((row) => row.amount),
              );
              return max > 0n
                ? formatSolanaTokenAmount(max, selectedToken.decimals)
                : null;
            }}
          />
        </>
      ) : (
        <>
          <input
            id="solana-send-recipient"
            style={styles.input}
            value={recipient}
            onChange={(event) => setRecipient(event.target.value)}
            placeholder="bob.stealth, private address or wallet address"
          />
          {handleStatus && (
            <p role="status" className="wallet-review-context">
              {handleStatus}
            </p>
          )}
          <label htmlFor="solana-send-amount" style={styles.label}>
            Amount ({selectedToken.symbol})
          </label>
          <WalletAmountInput
            id="solana-send-amount"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="0.01"
            disabled={disabled}
            onMax={() =>
              setAmount(
                formatSolanaTokenAmount(spendable, selectedToken.decimals),
              )
            }
            maxDisabled={spendable <= 0n}
            aria-invalid={showError && !!error}
            aria-describedby={
              showError && error ? "solana-send-error" : undefined
            }
          />
        </>
      )}
      <div style={{ fontSize: 12, color: "#b0c4de", marginTop: 4 }}>
        Private balance:{" "}
        {formatSolanaTokenAmount(spendable, selectedToken.decimals)}{" "}
        {selectedToken.symbol}
      </div>
      {showError && error && (
        <p
          id="solana-send-error"
          role="status"
          style={{ color: "#ff5263", fontSize: 13, margin: "6px 0 12px" }}
        >
          {error}
        </p>
      )}
      {!batchMode && isSolanaHandleInput(recipient) && (
        <>
          <label htmlFor="solana-send-memo" style={styles.label}>
            Memo (private, optional)
          </label>
          <input
            id="solana-send-memo"
            style={styles.input}
            value={memo}
            onChange={(event) => setMemo(event.target.value)}
            placeholder="thanks for dinner"
          />
        </>
      )}
      <SolanaSendFeeSummary
        token={selectedToken}
        preview={preview}
        quote={feeQuote.quote?.quote ?? null}
        error={feeQuote.error}
        batch={batchMode}
        privateTransfer={!batchMode && recipientKind === "private"}
      />
      {(feeQuote.error || (batchMode && feeQuote.batchMaxError)) && (
        <button
          type="button"
          className="wallet-secondary"
          onClick={feeQuote.refresh}
        >
          Retry fee quote
        </button>
      )}
      <button
        type="button"
        className="wallet-primary"
        disabled={disabled || !!error}
        onClick={() => void (batchMode ? onBatchSend() : onSend())}
      >
        {busy ? "Working…" : "Continue"}
      </button>
      {retiredTrees.length > 0 ? (
        <div
          style={{
            marginTop: "20px",
            padding: "12px",
            borderRadius: "10px",
            border: "1px solid rgba(251,191,36,0.35)",
            background: "rgba(251,191,36,0.06)",
          }}
        >
          <p
            style={{
              margin: "0 0 8px",
              color: "#fbbf24",
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            Notes in retired trees must be migrated before they can be sent.
          </p>
          {retiredTrees.map((entry) => (
            <div
              key={`${entry.treeIndex}:${entry.token.symbol}`}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                color: "#e8eeff",
                fontSize: "13px",
                padding: "4px 0",
              }}
            >
              <span>
                Tree {entry.treeIndex}: {entry.total} {entry.token.symbol} in{" "}
                {entry.count} note{entry.count === 1 ? "" : "s"}
              </span>
              <button
                type="button"
                disabled={disabled}
                onClick={() => void onMigrate(entry.treeIndex, entry.token)}
                style={{
                  ...styles.button,
                  ...styles.secondaryButton,
                  width: "auto",
                  padding: "6px 10px",
                  fontSize: "12px",
                  opacity: disabled ? 0.6 : 1,
                }}
              >
                Migrate
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
